// Case-insensitive substring matching for autocompletes and filter inputs. A
// candidate matches only when it contains the query verbatim — simpler and more
// predictable than fuzzy (subsequence) matching. No external dep — the option
// lists are small (metric names, aggregations, group prefixes).

/**
 * True when `candidate` contains `query` as a case-insensitive substring. An
 * empty query matches everything.
 */
export function isSubstringMatch(query: string, candidate: string): boolean {
  return candidate.toLowerCase().includes(query.toLowerCase());
}

/**
 * Filter `options` to those containing `query` as a case-insensitive
 * substring. Options whose match starts earlier rank first (so prefix matches
 * lead); ties keep their original relative order. An empty query returns the
 * options unchanged (stable order).
 */
export function substringFilter(query: string, options: string[]): string[] {
  if (query.trim() === "") {
    return [...options];
  }
  const q = query.toLowerCase();
  const matched: { option: string; index: number }[] = [];
  for (const option of options) {
    const index = option.toLowerCase().indexOf(q);
    if (index !== -1) {
      matched.push({ option, index });
    }
  }
  matched.sort((a, b) => a.index - b.index);
  return matched.map((entry) => entry.option);
}

/** A half-open `[start, end)` character range within a candidate string. */
export interface MatchRange {
  start: number;
  /** Exclusive. */
  end: number;
}

export interface SubstringMatchRangeOptions {
  /**
   * When `candidate` does not contain the whole query, fall back to the longest
   * prefix of the query that it does contain. Set this for ancestor nodes in
   * the metric tree, whose path only covers the start of the full query: the
   * branch `imu.accel` can never contain a query of `accel.x`, so exact-only
   * matching would leave that row unhighlighted. Leave it off when every
   * candidate is a whole metric name that already contains the whole query.
   */
  partial?: boolean;
}

/**
 * Locate the first case-insensitive occurrence of `query` within `candidate`.
 * Substring matches are contiguous by construction, so one range describes the
 * hit completely. Returns `null` when there is nothing to mark: an empty query,
 * or a candidate that does not contain the query (see `partial`).
 */
export function substringMatchRange(
  query: string,
  candidate: string,
  { partial = false }: SubstringMatchRangeOptions = {},
): MatchRange | null {
  const q = query.toLowerCase();
  const c = candidate.toLowerCase();
  if (q === "") {
    // `indexOf("")` is 0, so without this the loop would report an empty match
    // at the start of every candidate.
    return null;
  }
  // Longest-first, so a candidate holding the whole query always reports the
  // whole query. Without `partial` the loop body runs exactly once.
  const shortest = partial ? 1 : q.length;
  for (let length = q.length; length >= shortest; length--) {
    const start = c.indexOf(q.slice(0, length));
    if (start !== -1) {
      return { start, end: start + length };
    }
  }
  return null;
}
